import type {
  AlternateSelf,
  SelfComparison,
  AxisComparison,
  AxisId,
  SelvesInput,
} from "./types";
import { AXIS_ORDER } from "./types";
import { AXIS_LABELS, FICTION_NOTICE, displayName } from "./options";
import { type UniverseConfig, encodeConfig, decodeConfig } from "@/lib/universe";

/**
 * Build Link destination for opening a universe configuration in Create Your Universe.
 */
export function universeLinkFor(config: UniverseConfig): string {
  return `/CreateYourUniversePage?u=${encodeConfig(config)}`;
}

/**
 * Null-safe wrapper for decoding a ?u= universe parameter.
 */
export function parseUniverseParam(raw: string | null): UniverseConfig | null {
  if (!raw) return null;
  return decodeConfig(raw);
}

/**
 * Compare two alternate selves in depth according to the spec.
 */
export function compareSelves(a: AlternateSelf, b: AlternateSelf): SelfComparison {
  const axes: AxisComparison[] = AXIS_ORDER.map((axis) => {
    const valA = a.axes[axis];
    const valB = b.axes[axis];
    const delta = valA - valB;
    const absDelta = Math.abs(delta);
    const label = AXIS_LABELS[axis];

    let text: string;
    if (absDelta === 0) {
      text = `Both selves demonstrate equal expression of ${label.toLowerCase()} (${valA}/100).`;
    } else if (delta > 0) {
      text = `${a.name} rates higher in ${label.toLowerCase()} by ${absDelta} points (${valA} vs ${valB}).`;
    } else {
      text = `${b.name} rates higher in ${label.toLowerCase()} by ${absDelta} points (${valB} vs ${valA}).`;
    }

    return {
      axis,
      label,
      a: valA,
      b: valB,
      delta,
      text,
    };
  });

  // Find biggest divergence
  let maxDeltaAxis = axes[0];
  for (const item of axes) {
    if (Math.abs(item.delta) > Math.abs(maxDeltaAxis.delta)) {
      maxDeltaAxis = item;
    }
  }

  const leader = maxDeltaAxis.delta > 0 ? a.name : b.name;
  const follower = maxDeltaAxis.delta > 0 ? b.name : a.name;
  const biggestDivergence = `The sharpest contrast appears in ${maxDeltaAxis.label}, where ${leader} exceeds ${follower} by ${Math.abs(maxDeltaAxis.delta)} points.`;

  // Find shared ground: axes within 10 points or similar universe condition
  const closeAxes = axes.filter((it) => Math.abs(it.delta) <= 10);
  let sharedGround: string;
  if (closeAxes.length > 0) {
    const labels = closeAxes.map((c) => c.label).join(" and ");
    sharedGround = `Both timelines remain anchored by comparable ratings in ${labels}, preserving recognizable personal coherence.`;
  } else if (a.universe.condition === b.universe.condition) {
    sharedGround = `While their personal inclinations diverge, both navigate universes characterized by ${a.universe.condition} environmental conditions.`;
  } else {
    sharedGround = `Despite diverging temperaments, both manifestations channel a shared origin into purposeful engagement with their respective worlds.`;
  }

  // Universe contrast
  const universeContrast = `${a.name} inhabits "${a.universe.name}" (${a.universe.condition}, habitability ${a.universe.profile.habitability}/100, destined for ${a.universe.profile.fate}), whereas ${b.name} walks "${b.universe.name}" (${b.universe.condition}, habitability ${b.universe.profile.habitability}/100, evolving toward ${b.universe.profile.fate}).`;

  // Choice contrast
  const choiceA = a.choices[0] ? `faced "${a.choices[0].choice}" at ${a.choices[0].age}` : "navigated early pivotal junctures";
  const choiceB = b.choices[0] ? `confronted "${b.choices[0].choice}" at ${b.choices[0].age}` : "took an alternate branch";
  const choiceContrast = `Where ${a.name} ${choiceA}, ${b.name} instead ${choiceB}, steering their life trajectories along fundamentally different cosmological courses.`;

  const summary = `${a.name} (${a.scenarioLabel}) and ${b.name} (${b.scenarioLabel}) reflect contrasting expressions of identity: one prioritizing ${a.tagline.toLowerCase().replace(/\.$/, "")}, while the other embodies ${b.tagline.toLowerCase().replace(/\.$/, "")}.`;

  return {
    summary,
    axes,
    sharedGround,
    biggestDivergence,
    universeContrast,
    choiceContrast,
  };
}

/**
 * Plain-text share summary for a single alternate self.
 * Starts with FICTION_NOTICE and ends with the shareUrl.
 * When self.life is present (unlocked only), includes a concise life summary
 * (age, standing, society headline, three future path names).
 */
export function buildSelfShareText(self: AlternateSelf, input: SelvesInput, shareUrl: string): string {
  const visitor = displayName(input);
  const traitsText = self.traits.map((t) => `  • ${t.name} (${t.level}/100): ${t.description}`).join("\n");
  const axesText = AXIS_ORDER.map((ax) => `  • ${AXIS_LABELS[ax]}: ${self.axes[ax]}/100`).join("\n");
  const choicesText = self.choices.map((c) => `  [Age ${c.age}] ${c.choice} (Road not taken: ${c.roadNotTaken}) -> ${c.consequence}`).join("\n");
  const worldText = self.worldDetails.map((w) => `  • ${w.label}: ${w.value}`).join("\n");

  const dossierBlocks: string[] = [];
  if (self.dossier) {
    const d = self.dossier;
    if (d.background) {
      dossierBlocks.push(`BACKGROUND:\n${d.background}`);
    }
    if (d.personality) {
      dossierBlocks.push(`PERSONALITY:\n${d.personality}`);
    }
    if (d.skills && d.skills.length > 0) {
      dossierBlocks.push(`SKILLS:\n${d.skills.map((s) => `  • ${s}`).join("\n")}`);
    }
    if (d.motivations && d.motivations.length > 0) {
      dossierBlocks.push(`MOTIVATIONS:\n${d.motivations.map((m) => `  • ${m}`).join("\n")}`);
    }
    if (d.relationships && d.relationships.length > 0) {
      const relsText = d.relationships
        .map((r) => `  • ${r.who} (${r.role}): ${r.bond}`)
        .join("\n");
      dossierBlocks.push(`KEY RELATIONSHIPS:\n${relsText}`);
    }
    if (d.achievements && d.achievements.length > 0) {
      dossierBlocks.push(`ACHIEVEMENTS:\n${d.achievements.map((a) => `  • ${a}`).join("\n")}`);
    }
    if (d.failures && d.failures.length > 0) {
      dossierBlocks.push(`FAILURES:\n${d.failures.map((f) => `  • ${f}`).join("\n")}`);
    }
    if (d.dilemma) {
      dossierBlocks.push(`DEFINING DILEMMA:\n${d.dilemma}`);
    }
    if (d.trajectory) {
      dossierBlocks.push(
        `TRAJECTORY:\nNear Future: ${d.trajectory.nearFuture}\nLong Arc: ${d.trajectory.longArc}`
      );
    }
    if (d.influence && d.influence.length > 0) {
      dossierBlocks.push(
        `HOW YOUR ANSWERS SHAPED THIS:\n${d.influence.map((inf) => `  • ${inf}`).join("\n")}`
      );
    }
  }

  // Unlocked Multiverse Life Dossier section
  if (self.life) {
    const l = self.life;
    const futureNames = l.futures?.paths?.map((p) => p.name).join(", ") || "None";
    const lifeBlock = [
      `MULTIVERSE LIFE DOSSIER:`,
      `Age: ${l.identity?.age ?? "N/A"} (${l.identity?.ageNote ?? ""})`,
      `Standing: ${l.standing?.socialClass ?? ""} — ${l.standing?.wealthBracket ?? ""}`,
      `Society: ${l.society?.summary ?? ""}`,
      `Three Forward Horizons: ${futureNames}`,
    ].join("\n");
    dossierBlocks.push(lifeBlock);
  }

  const dossierSection = dossierBlocks.length > 0 ? `\n\n${dossierBlocks.join("\n\n")}` : "";

  return `${FICTION_NOTICE}

QUANTUM LIAISON — MULTIVERSE LIFE DOSSIER SPECIMEN
Explorer: ${visitor}
Self: ${self.name} — ${self.title}
Timeline: ${self.timelineId}
Archetype: ${self.scenarioLabel}
Tagline: "${self.tagline}"
Occupation: ${self.occupation}

CORE AXES:
${axesText}

DISTINCTIVE TRAITS:
${traitsText}

BIOGRAPHICAL STORY:
Origin: ${self.story.origin}
Turning Point: ${self.story.turningPoint}
Present Day: ${self.story.today}

PIVOTAL CHOICES:
${choicesText}

COSMOS & HABITAT:
Universe: ${self.universe.name} [${self.universe.code}]
Condition: ${self.universe.condition.toUpperCase()} (Habitability Index: ${self.universe.profile.habitability}/100)
Cosmic Fate: ${self.universe.profile.fate.toUpperCase()}
Description: ${self.universe.description}

WORLD DETAILS:
${worldText}${dossierSection}

EXPLORE IN BROWSER:
${shareUrl}`;
}

/**
 * Plain-text share summary for all four alternate selves.
 * Starts with FICTION_NOTICE and ends with the shareUrl.
 * When selves are unlocked with self.life, includes a concise life summary.
 */
export function buildAllShareText(selves: AlternateSelf[], input: SelvesInput, shareUrl: string): string {
  const visitor = displayName(input);
  const blocks = selves.map((self, idx) => {
    const dilemmaLine = self.dossier?.dilemma ? `\nDilemma: ${self.dossier.dilemma}` : "";
    let lifeSummary = "";
    if (self.life) {
      const l = self.life;
      const futureNames = l.futures?.paths?.map((p) => p.name).join(", ") || "";
      lifeSummary = `\nLife: Age ${l.identity?.age ?? "N/A"} · Standing: ${l.standing?.wealthBracket ?? l.standing?.socialClass ?? ""} · Futures: ${futureNames}`;
    }
    return `[${idx + 1}] ${self.name} (${self.scenarioLabel})
Title: ${self.title}
Timeline: ${self.timelineId}
Occupation: ${self.occupation}${dilemmaLine}${lifeSummary}
Cosmos: ${self.universe.name} (${self.universe.condition}, habitability ${self.universe.profile.habitability}/100)
Tagline: "${self.tagline}"`;
  }).join("\n\n");

  return `${FICTION_NOTICE}

QUANTUM LIAISON — YOUR FOUR MULTIVERSE LIFE DOSSIERS
Explorer: ${visitor}
Four fictional timelines inspired by cosmological multiverse theory:

${blocks}

EXPLORE AND COMPARE ALL FOUR:
${shareUrl}`;
}
